/**
 * MarkApprovals — R29
 * Mark's approval queue for drivers and vehicles that Karishma proposed via
 * the /portal/karishma flow (R28). Consumes existing endpoints:
 *   GET  /api/karishma/oversight/pending
 *   POST /api/karishma/oversight/approve  body {kind:'driver'|'vehicle', proposal_id}
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export default function MarkApprovals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/karishma/oversight/pending");
      setDrivers(res.data.drivers || []);
      setVehicles(res.data.vehicles || []);
    } catch (err) {
      const msg = err?.response?.data?.error?.message
               || err?.response?.data?.detail
               || err?.message || "Could not load approvals";
      toast.error(msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (kind, id) => {
    setBusyId(id);
    try {
      await api.post("/karishma/oversight/approve", { kind, proposal_id: id });
      toast.success(kind === "driver" ? "Driver approved" : "Vehicle approved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || err?.response?.data?.detail || "Approve failed");
    } finally { setBusyId(null); }
  };

  const reject = async (kind, id, label) => {
    const reason = window.prompt(`Reject this ${kind}${label ? ' (' + label + ')' : ''}? Add a short reason (optional):`, "");
    if (reason === null) return;  // user cancelled
    setBusyId(id);
    try {
      await api.post("/karishma/oversight/reject", { kind, proposal_id: id, reason: reason || null });
      toast.success(kind === "driver" ? "Driver rejected" : "Vehicle rejected");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || err?.response?.data?.detail || "Reject failed");
    } finally { setBusyId(null); }
  };

  const total = drivers.length + vehicles.length;

  return (
    <div data-testid="mark-approvals-page" className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 text-white py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Karishma proposals — approvals</h1>
            <p className="text-sm text-slate-400">Review drivers and vehicles Karishma has proposed. Approving flips them to active.</p>
          </div>
          <button
            data-testid="mark-approvals-back"
            onClick={() => navigate("/admin/vehicle-ops")}
            className="text-sm text-slate-300 hover:text-white"
          >← Vehicle Ops</button>
        </div>

        <div data-testid="mark-approvals-summary" className="mb-4 text-sm text-slate-300">
          {loading ? "Loading…" : (
            <>Pending: <span data-testid="mark-approvals-count" className="text-emerald-300 font-semibold">{total}</span> ({drivers.length} drivers, {vehicles.length} vehicles)</>
          )}
        </div>

        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-3">Drivers</h2>
          {!loading && drivers.length === 0 && (
            <div data-testid="mark-approvals-empty-drivers" className="text-sm text-slate-500 italic">No pending drivers.</div>
          )}
          <div className="space-y-3">
            {drivers.map((d) => (
              <div key={d.id} data-testid={`mark-approvals-driver-${d.id}`} className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
                <div className="font-semibold text-white">{d.name || "(no name)"}</div>
                <div className="text-xs text-slate-400 mt-1">
                  {d.phone && <>Phone: {d.phone} · </>}
                  {d.email && <>Email: {d.email} · </>}
                  {d.license_plate_hint && <>Plate hint: {d.license_plate_hint}</>}
                </div>
                {d.notes && <div className="text-xs text-slate-300 mt-2 italic">"{d.notes}"</div>}
                <div className="mt-3 flex gap-2">
                  <button
                    data-testid={`mark-approvals-approve-driver-${d.id}`}
                    onClick={() => approve("driver", d.id)}
                    disabled={busyId === d.id}
                    className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
                  >{busyId === d.id ? "Approving…" : "Approve"}</button>
                  <button
                    data-testid={`mark-approvals-reject-driver-${d.id}`}
                    onClick={() => reject("driver", d.id, d.name)}
                    disabled={busyId === d.id}
                    className="px-4 py-2 rounded-lg border border-rose-400/40 text-rose-300 hover:bg-rose-500/10 text-sm font-medium disabled:opacity-50"
                  >Reject</button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">Vehicles</h2>
          {!loading && vehicles.length === 0 && (
            <div data-testid="mark-approvals-empty-vehicles" className="text-sm text-slate-500 italic">No pending vehicles.</div>
          )}
          <div className="space-y-3">
            {vehicles.map((v) => (
              <div key={v.id} data-testid={`mark-approvals-vehicle-${v.id}`} className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
                <div className="font-semibold text-white">
                  {[v.year, v.colour, v.vehicle_make, v.vehicle_model].filter(Boolean).join(" ") || "(vehicle)"}
                </div>
                <div className="text-xs text-slate-400 mt-1">Plate: {v.license_plate || "(none)"}</div>
                {v.notes && <div className="text-xs text-slate-300 mt-2 italic">"{v.notes}"</div>}
                <div className="mt-3 flex gap-2">
                  <button
                    data-testid={`mark-approvals-approve-vehicle-${v.id}`}
                    onClick={() => approve("vehicle", v.id)}
                    disabled={busyId === v.id}
                    className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
                  >{busyId === v.id ? "Approving…" : "Approve"}</button>
                  <button
                    data-testid={`mark-approvals-reject-vehicle-${v.id}`}
                    onClick={() => reject("vehicle", v.id, v.license_plate)}
                    disabled={busyId === v.id}
                    className="px-4 py-2 rounded-lg border border-rose-400/40 text-rose-300 hover:bg-rose-500/10 text-sm font-medium disabled:opacity-50"
                  >Reject</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
